'use client';
import React, { useState } from 'react';

const orderCards = [
  {
    emoji: '🍔',
    name: 'Smash Burger',
    status: 'On the way',
    time: '12 min',
    statusColor: '#f97316',
    bg: '#1a1a1a',
  },
  {
    emoji: '🍜',
    name: 'Ramen Bowl',
    status: 'Arriving soon',
    time: '8 min',
    statusColor: '#22c55e',
    bg: '#1a1a1a',
  },
  {
    emoji: '🛒',
    name: 'Grocery Run',
    status: 'Picked up',
    time: '20 min',
    statusColor: '#22c55e',
    bg: '#1a1a1a',
  },
  {
    emoji: '💊',
    name: 'Pharmacy',
    status: 'On the way',
    time: '15 min',
    statusColor: '#f97316',
    bg: '#1a1a1a',
  },
];

export default function HeroSection() {
  const [showMessage, setShowMessage] = useState(false);

  const handleBlockedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowMessage(true);
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 pb-16 overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#f97316]/10 blur-[100px] rounded-full pointer-events-none" />

      {/* Decorative floating orbs */}
      <div className="absolute top-24 left-10 w-3 h-3 rounded-full bg-[#f97316]/60 animate-pulse pointer-events-none" />
      <div className="absolute top-40 left-24 w-1.5 h-1.5 rounded-full bg-[#f5c518]/80 animate-pulse pointer-events-none" style={{ animationDelay: '0.5s' }} />
      <div className="absolute top-32 right-16 w-2 h-2 rounded-full bg-[#f97316]/50 animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
      <div className="absolute top-60 right-32 w-1 h-1 rounded-full bg-[#f5c518]/70 animate-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />
      <div className="absolute bottom-40 left-16 w-2 h-2 rounded-full bg-[#f97316]/40 animate-pulse pointer-events-none" style={{ animationDelay: '0.8s' }} />
      <div className="absolute bottom-60 right-20 w-1.5 h-1.5 rounded-full bg-[#f5c518]/60 animate-pulse pointer-events-none" style={{ animationDelay: '1.2s' }} />

      {/* Decorative grid lines */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(249,115,22,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.03) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Decorative corner accents */}
      <div className="absolute top-20 left-0 w-32 h-32 pointer-events-none">
        <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-[#f97316]/20 rounded-tl-lg" />
      </div>
      <div className="absolute top-20 right-0 w-32 h-32 pointer-events-none">
        <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-[#f5c518]/20 rounded-tr-lg" />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center px-4 max-w-4xl mx-auto">
        {/* Badge */}
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1a0e00] border border-[#3d2000] text-[#f97316] text-sm font-medium">
            <span>🛵</span>
            <span>Now delivering in your area</span>
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
          Food, groceries
          <br />
          <span className="text-white">&amp; more — </span>
          <span
            style={{
              background: 'linear-gradient(135deg, #f97316, #f5c518)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontStyle: 'italic',
            }}
          >
            delivered fast.
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-gray-400 text-lg md:text-xl mb-10 max-w-lg">
          Shop Easily, Sell Faster. Deliver Everywhere.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col items-center gap-4 w-full max-w-md">
          {/* Row 1 */}
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <button
              onClick={handleBlockedClick}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-black text-base transition-all duration-200 hover:scale-105 hover:brightness-110 shadow-lg shadow-[#f97316]/20"
              style={{ background: 'linear-gradient(135deg, #f5c518, #f97316)' }}
            >
              <span>🛒</span>
              <span>Start ordering</span>
            </button>
            <button
              onClick={handleBlockedClick}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-white text-base bg-[#1e1e1e] border border-[#333] hover:bg-[#2a2a2a] hover:border-[#f97316]/30 transition-all duration-200"
            >
              See how it works
            </button>
          </div>

          {/* Row 2 - Fleet Manager */}
          <button
            onClick={handleBlockedClick}
            className="flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-white text-base transition-all duration-200 hover:scale-105 hover:brightness-110 shadow-lg shadow-[#4f6ef7]/20"
            style={{ background: 'linear-gradient(135deg, #4f6ef7, #7c3aed)' }}
          >
            <span>🚀</span>
            <span>Fleet Manager Portal</span>
          </button>

          {/* Row 3 */}
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <button
              onClick={handleBlockedClick}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-black text-base transition-all duration-200 hover:scale-105 shadow-lg shadow-[#38bdf8]/20"
              style={{ background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)' }}
            >
              <span>📱</span>
              <span>Download the app</span>
            </button>
          </div>
        </div>

        {/* Blocked navigation message */}
        {showMessage && (
          <div className="mt-6 max-w-md w-full bg-[#1a0e00] border border-[#f97316]/40 rounded-2xl px-6 py-4 flex items-start gap-3">
            <span className="text-[#f97316] text-xl mt-0.5 flex-shrink-0">⚠️</span>
            <div className="text-left">
              <p className="text-gray-300 text-sm leading-relaxed">
                Sorry but try again later. For this app is still being polished — making it ready for public use and for more information, reach out to us through the{' '}
                <a href="/contact" className="text-[#f97316] underline hover:text-[#f5c518] transition-colors">
                  contact page
                </a>
                .
              </p>
              <button
                onClick={() => setShowMessage(false)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Live Order Cards */}
        <div className="mt-12 flex flex-wrap justify-center gap-3 w-full max-w-3xl">
          {orderCards?.map((card, i) => (
            <div
              key={i}
              className="flex items-center gap-3 bg-[#161616] border border-[#252525] rounded-xl px-4 py-3 min-w-[160px]"
            >
              <div className="w-9 h-9 rounded-lg bg-[#222] flex items-center justify-center text-xl flex-shrink-0">
                {card?.emoji}
              </div>
              <div>
                <p className="text-white text-sm font-semibold leading-tight">{card?.name}</p>
                <p className="text-xs mt-0.5" style={{ color: card?.statusColor }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: card?.statusColor }} />
                  {card?.status} · {card?.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
